import bs58 from 'bs58'
import nacl from 'tweetnacl'

const PHANTOM_CONNECT_URL = 'https://phantom.app/ul/v1/connect'
const PHANTOM_SIGN_TRANSACTION_URL = 'https://phantom.app/ul/v1/signTransaction'
const PHANTOM_REQUEST_ID_PARAM = 'phantomRequestId'

export const PHANTOM_SOLANA_SESSION_STORAGE_KEY =
  'paytrie:phantom-solana-mobile:session'
export const PHANTOM_SOLANA_PENDING_STORAGE_KEY =
  'paytrie:phantom-solana-mobile:pending'

const CALLBACK_PARAMS = [
  'data',
  'errorCode',
  'errorMessage',
  'nonce',
  'phantom_encryption_public_key',
  PHANTOM_REQUEST_ID_PARAM,
] as const

export type PhantomSolanaSession = {
  dappEncryptionPublicKey: string
  dappEncryptionSecretKey: string
  phantomEncryptionPublicKey: string
  session: string
  walletPublicKey: string
}

export type PhantomSolanaPendingAction =
  | {
      dappEncryptionPublicKey: string
      dappEncryptionSecretKey: string
      type: 'connect'
    }
  | {
      orderId: string
      type: 'signTransaction'
    }

type PhantomConnectPayload = {
  public_key: string
  session: string
}

type PhantomSignTransactionPayload = {
  transaction: string
}

type Storage = Pick<
  globalThis.Storage,
  'getItem' | 'key' | 'length' | 'removeItem' | 'setItem'
>

const encodeJson = (value: unknown) =>
  Uint8Array.from(new TextEncoder().encode(JSON.stringify(value)))

const decodeJson = <T>(value: Uint8Array) =>
  JSON.parse(new TextDecoder().decode(value)) as T

const getPendingStorageKey = (requestId: string) =>
  `${PHANTOM_SOLANA_PENDING_STORAGE_KEY}:${requestId}`

const createRequestId = () => bs58.encode(nacl.randomBytes(16))

const getCallbackUrl = (location: Location, requestId: string) => {
  const url = new URL(location.href)
  CALLBACK_PARAMS.forEach((param) => url.searchParams.delete(param))
  url.searchParams.set(PHANTOM_REQUEST_ID_PARAM, requestId)
  return url.toString()
}

const getSharedSecret = ({
  dappEncryptionSecretKey,
  phantomEncryptionPublicKey,
}: Pick<
  PhantomSolanaSession,
  'dappEncryptionSecretKey' | 'phantomEncryptionPublicKey'
>) =>
  nacl.box.before(
    bs58.decode(phantomEncryptionPublicKey),
    bs58.decode(dappEncryptionSecretKey)
  )

const decryptPayload = <T>({
  data,
  nonce,
  sharedSecret,
}: {
  data: string
  nonce: string
  sharedSecret: Uint8Array
}) => {
  const decrypted = nacl.box.open.after(
    bs58.decode(data),
    bs58.decode(nonce),
    sharedSecret
  )

  if (!decrypted) {
    throw new Error('Phantom returned an invalid encrypted response')
  }

  return decodeJson<T>(decrypted)
}

const encryptPayload = ({
  payload,
  sharedSecret,
}: {
  payload: unknown
  sharedSecret: Uint8Array
}) => {
  const nonce = nacl.randomBytes(nacl.box.nonceLength)
  const encryptedPayload = nacl.box.after(
    encodeJson(payload),
    nonce,
    sharedSecret
  )

  return {
    nonce: bs58.encode(nonce),
    payload: bs58.encode(encryptedPayload),
  }
}

export const getStoredPhantomSolanaSession = (
  storage: Storage = localStorage
) => {
  const value = storage.getItem(PHANTOM_SOLANA_SESSION_STORAGE_KEY)
  if (!value) return null

  try {
    return JSON.parse(value) as PhantomSolanaSession
  } catch {
    storage.removeItem(PHANTOM_SOLANA_SESSION_STORAGE_KEY)
    return null
  }
}

export const clearPhantomSolanaSession = (storage: Storage = localStorage) => {
  storage.removeItem(PHANTOM_SOLANA_SESSION_STORAGE_KEY)
  storage.removeItem(PHANTOM_SOLANA_PENDING_STORAGE_KEY)
  Array.from({ length: storage.length }, (_, index) => storage.key(index))
    .filter(
      (key): key is string =>
        key !== null && key.startsWith(`${PHANTOM_SOLANA_PENDING_STORAGE_KEY}:`)
    )
    .forEach((key) => storage.removeItem(key))
}

export const getPhantomSolanaConnectUrl = ({
  cluster,
  location = window.location,
  storage = localStorage,
}: {
  cluster: 'devnet' | 'mainnet-beta' | 'testnet'
  location?: Location
  storage?: Storage
}) => {
  const keyPair = nacl.box.keyPair()
  const dappEncryptionPublicKey = bs58.encode(keyPair.publicKey)
  const dappEncryptionSecretKey = bs58.encode(keyPair.secretKey)
  const requestId = createRequestId()
  const pendingAction: PhantomSolanaPendingAction = {
    dappEncryptionPublicKey,
    dappEncryptionSecretKey,
    type: 'connect',
  }

  storage.setItem(
    getPendingStorageKey(requestId),
    JSON.stringify(pendingAction)
  )

  const url = new URL(PHANTOM_CONNECT_URL)
  url.searchParams.set('app_url', location.origin)
  url.searchParams.set('dapp_encryption_public_key', dappEncryptionPublicKey)
  url.searchParams.set('redirect_link', getCallbackUrl(location, requestId))
  url.searchParams.set('cluster', cluster)
  return url.toString()
}

export const getPhantomSolanaSignTransactionUrl = ({
  location = window.location,
  orderId,
  serializedTransaction,
  session,
  storage = localStorage,
}: {
  location?: Location
  orderId: string
  serializedTransaction: Uint8Array
  session: PhantomSolanaSession
  storage?: Storage
}) => {
  const sharedSecret = getSharedSecret(session)
  const requestId = createRequestId()
  const encrypted = encryptPayload({
    payload: {
      session: session.session,
      transaction: bs58.encode(serializedTransaction),
    },
    sharedSecret,
  })
  const pendingAction: PhantomSolanaPendingAction = {
    orderId,
    type: 'signTransaction',
  }

  storage.setItem(
    getPendingStorageKey(requestId),
    JSON.stringify(pendingAction)
  )

  const url = new URL(PHANTOM_SIGN_TRANSACTION_URL)
  url.searchParams.set(
    'dapp_encryption_public_key',
    session.dappEncryptionPublicKey
  )
  url.searchParams.set('nonce', encrypted.nonce)
  url.searchParams.set('payload', encrypted.payload)
  url.searchParams.set('redirect_link', getCallbackUrl(location, requestId))
  return url.toString()
}

export type PhantomSolanaCallbackResult =
  | {
      session: PhantomSolanaSession
      type: 'connect'
    }
  | {
      orderId: string
      serializedTransaction: Uint8Array
      type: 'signTransaction'
    }
  | null

export const consumePhantomSolanaCallback = ({
  location = window.location,
  storage = localStorage,
}: {
  location?: Location
  storage?: Storage
} = {}): PhantomSolanaCallbackResult => {
  const url = new URL(location.href)
  const requestId = url.searchParams.get(PHANTOM_REQUEST_ID_PARAM)
  if (!requestId) return null

  const pendingStorageKey = getPendingStorageKey(requestId)
  const pendingValue = storage.getItem(pendingStorageKey)
  const errorCode = url.searchParams.get('errorCode')
  const errorMessage = url.searchParams.get('errorMessage')
  const data = url.searchParams.get('data')
  if (!pendingValue || (!data && !errorCode && !errorMessage)) return null

  storage.removeItem(pendingStorageKey)

  if (errorCode || errorMessage) {
    throw new Error(errorMessage ?? `Phantom returned error ${errorCode}`)
  }
  if (!data) throw new Error('Phantom response is missing its encrypted data')

  const nonce = url.searchParams.get('nonce')
  if (!nonce) throw new Error('Phantom response is missing its nonce')

  const pending = JSON.parse(pendingValue) as PhantomSolanaPendingAction

  if (pending.type === 'connect') {
    const phantomEncryptionPublicKey = url.searchParams.get(
      'phantom_encryption_public_key'
    )
    if (!phantomEncryptionPublicKey) {
      throw new Error('Phantom response is missing its encryption public key')
    }
    const sharedSecret = getSharedSecret({
      dappEncryptionSecretKey: pending.dappEncryptionSecretKey,
      phantomEncryptionPublicKey,
    })
    const payload = decryptPayload<PhantomConnectPayload>({
      data,
      nonce,
      sharedSecret,
    })
    const session: PhantomSolanaSession = {
      dappEncryptionPublicKey: pending.dappEncryptionPublicKey,
      dappEncryptionSecretKey: pending.dappEncryptionSecretKey,
      phantomEncryptionPublicKey,
      session: payload.session,
      walletPublicKey: payload.public_key,
    }
    storage.setItem(PHANTOM_SOLANA_SESSION_STORAGE_KEY, JSON.stringify(session))
    return { session, type: 'connect' }
  }

  const session = getStoredPhantomSolanaSession(storage)
  if (!session) {
    clearPhantomSolanaSession(storage)
    throw new Error('The Phantom session is no longer available')
  }

  try {
    const payload = decryptPayload<PhantomSignTransactionPayload>({
      data,
      nonce,
      sharedSecret: getSharedSecret(session),
    })
    return {
      orderId: pending.orderId,
      serializedTransaction: bs58.decode(payload.transaction),
      type: 'signTransaction',
    }
  } catch (error) {
    clearPhantomSolanaSession(storage)
    throw error
  }
}

export const hasPhantomSolanaCallbackParams = (
  location: Location = window.location
) => {
  const url = new URL(location.href)
  return CALLBACK_PARAMS.some((param) => url.searchParams.has(param))
}

export const removePhantomSolanaCallbackParams = (
  location: Location = window.location,
  history: History = window.history
) => {
  const url = new URL(location.href)
  CALLBACK_PARAMS.forEach((param) => url.searchParams.delete(param))
  history.replaceState(history.state, '', url)
}

export const base64ToBytes = (value: string) => {
  const binary = atob(value)
  return Uint8Array.from(binary, (character) => character.charCodeAt(0))
}
